import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from "@mui/icons-material";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const CLOUD_FUNCTION_BASE =
  "https://us-central1-mylocalforce-295b8.cloudfunctions.net";
const GST_RATE = 0.1;

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;

  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const asFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCoords = (coords) => {
  if (!coords) return null;

  let lat = asFiniteNumber(coords.lat ?? coords.latitude ?? coords._lat);
  let lng = asFiniteNumber(
    coords.lng ??
      coords.longitude ??
      coords.lon ??
      coords.long ??
      coords._long ??
      coords._lng
  );

  if (lat === null || lng === null) return null;

  const latInRange = lat >= -90 && lat <= 90;
  const lngInRange = lng >= -180 && lng <= 180;
  const couldBeSwapped =
    (!latInRange && lng >= -90 && lng <= 90 && lat >= -180 && lat <= 180) ||
    (latInRange && lngInRange && Math.abs(lat) > 60 && Math.abs(lng) <= 60);

  if (couldBeSwapped) {
    const swappedLat = lng;
    const swappedLng = lat;
    lat = swappedLat;
    lng = swappedLng;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return { lat, lng };
};

const extractFromCoordinatePairString = (value) => {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const plainPairMatch = trimmed.match(
    /^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/
  );
  if (!plainPairMatch) return null;

  const first = asFiniteNumber(plainPairMatch[1]);
  const second = asFiniteNumber(plainPairMatch[2]);
  if (first === null || second === null) return null;

  if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return normalizeCoords({ lat: second, lng: first });
  }

  return normalizeCoords({ lat: first, lng: second });
};

const extractFromCoordinateArray = (value, isGeoJsonPoint = false) => {
  if (!Array.isArray(value) || value.length < 2) return null;

  const first = asFiniteNumber(value[0]);
  const second = asFiniteNumber(value[1]);
  if (first === null || second === null) return null;

  if (isGeoJsonPoint || (Math.abs(first) > 90 && Math.abs(second) <= 90)) {
    return normalizeCoords({ lat: second, lng: first });
  }

  return normalizeCoords({ lat: first, lng: second });
};

const extractCoords = (record = {}) => {
  if (!record || typeof record !== "object") return null;

  const nestedKeys = [
    "location",
    "coords",
    "customerCoords",
    "providerLocation",
    "ownerLocation",
    "geopoint",
    "geoPoint",
    "addressLocation",
    "selectedAddress",
    "profile",
    "provider_onboarding",
    "details",
    "customerLocation",
    "serviceLocation",
    "currentLocation",
    "position",
    "coordinates",
    "geo",
  ];

  const visited = new Set();
  const queue = [record];

  while (queue.length > 0) {
    const candidate = queue.shift();
    if (candidate == null) continue;

    if (typeof candidate === "string") {
      const fromString = extractFromCoordinatePairString(candidate);
      if (fromString) return fromString;
      continue;
    }

    if (Array.isArray(candidate)) {
      const fromArray = extractFromCoordinateArray(candidate);
      if (fromArray) return fromArray;

      for (const item of candidate) {
        if (item && (typeof item === "object" || typeof item === "string")) {
          queue.push(item);
        }
      }
      continue;
    }

    if (typeof candidate !== "object" || visited.has(candidate)) {
      continue;
    }
    visited.add(candidate);

    const direct = normalizeCoords(candidate);
    if (direct) return direct;

    if (Array.isArray(candidate.coordinates)) {
      const fromCoordinates = extractFromCoordinateArray(
        candidate.coordinates,
        String(candidate.type || "").toLowerCase() === "point"
      );
      if (fromCoordinates) return fromCoordinates;
    }

    for (const key of nestedKeys) {
      if (candidate[key] != null) {
        queue.push(candidate[key]);
      }
    }
  }

  return null;
};

const normalizeText = (value) => String(value || "").toLowerCase().trim();

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getProviderServiceDetails = (provider = {}) => {
  const combined = [
    ...normalizeList(provider.servicesOffered),
    ...normalizeList(provider.provider_onboarding?.servicesOffered),
    ...normalizeList(provider.profile?.servicesOffered),
    ...normalizeList(provider.serviceCategories),
    ...normalizeList(provider.serviceTypes),
    ...normalizeList(provider.skills),
    ...normalizeList(provider.categories),
  ];

  return Array.from(new Set(combined));
};

const getBookingServiceTerms = (booking = {}) => {
  const terms = [
    booking.serviceName,
    booking.subcategory,
    booking.leadSubcategory,
    booking.category,
    booking.leadCategory,
  ]
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  return Array.from(new Set(terms));
};

const providerMatchesBookingService = (provider = {}, bookingTerms = []) => {
  if (!bookingTerms.length) return true;

  const providerServices = getProviderServiceDetails(provider).map(normalizeText);
  if (!providerServices.length) return false;

  const normalizedBookingTerms = bookingTerms.map(normalizeText);
  return normalizedBookingTerms.some((term) =>
    providerServices.some((service) => service.includes(term) || term.includes(service))
  );
};

const formatDistanceLabel = (distance) =>
  Number.isFinite(distance) ? `${distance.toFixed(1)} km away` : "distance unavailable";

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'
  const [searchTerm, setSearchTerm] = useState("");
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [assigningProvider, setAssigningProvider] = useState(false);
  const [maxDistance, setMaxDistance] = useState(50);
  const [selectedBookingResolvedCoords, setSelectedBookingResolvedCoords] =
    useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");

    const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const bookingsData = querySnapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }));

        setBookings(bookingsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error subscribing to bookings:", err);
        setError("Failed to fetch bookings. Please try again.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadProviders = async () => {
      setProvidersLoading(true);

      try {
        const providersQuery = query(
          collection(db, "users"),
          where("roles.client", "==", true)
        );

        const providersSnapshot = await getDocs(providersQuery);
        let providerList = providersSnapshot.docs
          .map((providerDoc) => ({
            id: providerDoc.id,
            ...providerDoc.data(),
          }))
          .filter(
            (provider) =>
              (provider.approvalStatus === "approved" ||
                provider.isApproved === true) &&
              provider.accountActive !== false &&
              provider.inactive !== true &&
              provider.accountStatus !== "deactivated"
          )
          .sort((providerA, providerB) => {
            const nameA = String(
              providerA.displayName ||
                providerA.name ||
                providerA.businessName ||
                providerA.email ||
                ""
            ).toLowerCase();
            const nameB = String(
              providerB.displayName ||
                providerB.name ||
                providerB.businessName ||
                providerB.email ||
                ""
            ).toLowerCase();
            return nameA.localeCompare(nameB);
          });

        providerList = await Promise.all(
          providerList.map(async (provider) => {
            if (extractCoords(provider)) return provider;

            try {
              const onboardingSnapshot = await getDoc(
                doc(db, "users", provider.id, "details", "provider_onboarding")
              );

              if (!onboardingSnapshot.exists()) return provider;

              const onboardingData = onboardingSnapshot.data() || {};
              const onboardingCoords = extractCoords(onboardingData);

              return {
                ...provider,
                provider_onboarding: onboardingData,
                ...(onboardingCoords ? { providerLocation: onboardingCoords } : {}),
              };
            } catch (onboardingError) {
              console.warn(
                "Could not load provider onboarding details:",
                provider.id,
                onboardingError
              );
              return provider;
            }
          })
        );

        if (!isMounted) return;
        setProviders(providerList);
      } catch (providerError) {
        console.error("Error loading providers:", providerError);
        if (!isMounted) return;
        setProviders([]);
      } finally {
        if (isMounted) {
          setProvidersLoading(false);
        }
      }
    };

    loadProviders();

    return () => {
      isMounted = false;
    };
  }, []);

  // Deletion removed: bookings are not deletable from UI

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  // Format date helper
  const formatDate = (dateField) => {
    if (!dateField) return "N/A";
    if (dateField.toDate && typeof dateField.toDate === "function") {
      return dateField.toDate().toLocaleDateString();
    }
    if (dateField instanceof Date) {
      return dateField.toLocaleDateString();
    }
    if (typeof dateField === "string") {
      try {
        return new Date(dateField).toLocaleDateString();
      } catch {
        return dateField;
      }
    }
    // Support RN-style date map objects: { day, month, year }
    if (
      typeof dateField === "object" &&
      dateField !== null &&
      dateField.day !== undefined &&
      dateField.month !== undefined &&
      (dateField.year !== undefined || dateField.Y !== undefined)
    ) {
      const day = String(dateField.day).padStart(2, "0");
      const month = String(dateField.month).padStart(2, "0");
      const year = String(dateField.year || dateField.Y);
      return `${day}/${month}/${year}`;
    }
    return "N/A";
  };

  const formatDateTime = (dateField) => {
    if (!dateField) return "N/A";
    if (dateField.toDate && typeof dateField.toDate === "function") {
      return dateField.toDate().toLocaleString();
    }
    if (dateField instanceof Date) {
      return dateField.toLocaleString();
    }
    if (typeof dateField === "string") {
      const parsed = new Date(dateField);
      if (Number.isNaN(parsed.getTime())) return dateField;
      return parsed.toLocaleString();
    }
    return "N/A";
  };

  const formatCurrency = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "N/A";
    return `$${parsed.toFixed(2)}`;
  };

  const firstFiniteNumber = (...values) => {
    for (const value of values) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return NaN;
  };

  const getBookingPaymentBreakdown = (booking = {}) => {
    const platformFee = Math.max(
      0,
      firstFiniteNumber(booking.platformFee, booking.accounting?.platformFee, 0)
    );
    const serviceFee = Math.max(
      0,
      firstFiniteNumber(
        booking.serviceTotal,
        booking.price,
        booking.basePrice,
        0
      )
    );
    const servicePlusPlatform = serviceFee + platformFee;
    const resolvedTotal = Math.max(
      0,
      firstFiniteNumber(
        booking.amountBeforeStripe,
        booking.totalAmount,
        booking.estimatedTotalAmount,
        servicePlusPlatform,
        booking.totalAmountPaid,
        serviceFee,
        0
      )
    );
    const resolvedPlatformFee = platformFee || Math.max(0, resolvedTotal - serviceFee);
    const gstIncluded = serviceFee * (GST_RATE / (1 + GST_RATE));

    return {
      serviceFee,
      platformFee: resolvedPlatformFee,
      totalCharged: resolvedTotal,
      gstIncluded,
      netServicePrice: serviceFee - gstIncluded,
    };
  };

  const getProviderDisplayName = (provider = {}) =>
    provider.displayName ||
    provider.name ||
    provider.businessName ||
    provider.email ||
    "Unknown Provider";

  const getBookingTitle = (booking = {}) =>
    booking.serviceName || booking.serviceTitle || booking.subcategory || "Unknown Service";

  const getCustomerLabel = (booking = {}) =>
    booking.customerName || booking.customerEmail || booking.customerId || "Unknown Customer";

  const getProviderLabel = (booking = {}) =>
    booking.providerName || booking.clientName || booking.clientEmail || booking.clientId || "Unassigned";

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "requested":
      case "lead":
      case "pending":
        return "warning";
      case "upcoming":
      case "in_progress":
      case "pending_provider":
        return "info";
      case "accepted":
      case "confirmed":
        return "success";
      case "completed":
        return "primary";
      case "rejected":
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const hasProviderNotReachedAlert = (booking) => {
    const status = String(
      booking?.providerNotReachedAlert?.status ||
        booking?.providerNotReachedAlertStatus ||
        ""
    ).toLowerCase();

    if (!booking?.providerNotReachedAlert && !status) return false;
    if (!status) return true;
    return status !== "resolved" && status !== "closed";
  };

  const selectedBooking =
    bookings.find((booking) => booking.id === selectedBookingId) || null;

  useEffect(() => {
    let isMounted = true;

    const resolveBookingCoords = async () => {
      if (!selectedBooking) {
        if (isMounted) setSelectedBookingResolvedCoords(null);
        return;
      }

      const directCoords = extractCoords(selectedBooking);
      if (directCoords) {
        if (isMounted) setSelectedBookingResolvedCoords(directCoords);
        return;
      }

      const customerId = selectedBooking.customerId || selectedBooking.userId;
      if (!customerId) {
        if (isMounted) setSelectedBookingResolvedCoords(null);
        return;
      }

      try {
        const customerSnapshot = await getDoc(doc(db, "users", customerId));
        if (!customerSnapshot.exists()) {
          if (isMounted) setSelectedBookingResolvedCoords(null);
          return;
        }

        const customerCoords = extractCoords(customerSnapshot.data() || {});
        if (isMounted) {
          setSelectedBookingResolvedCoords(customerCoords || null);
        }
      } catch (coordsError) {
        console.warn(
          "Could not resolve booking coordinates from customer profile:",
          customerId,
          coordsError
        );
        if (isMounted) setSelectedBookingResolvedCoords(null);
      }
    };

    resolveBookingCoords();

    return () => {
      isMounted = false;
    };
  }, [selectedBooking]);

  const selectedBookingScheduledDate = selectedBooking
    ? formatDate(
        selectedBooking.scheduledDate ||
          selectedBooking.bookingDate ||
          selectedBooking.selectedDate ||
          selectedBooking.requestedDate ||
          selectedBooking.date
      )
    : "N/A";

  const selectedBookingScheduledTime = selectedBooking
    ? selectedBooking.selectedTime ||
      selectedBooking.requestedTime ||
      selectedBooking.time ||
      selectedBooking.scheduledTime ||
      "N/A"
    : "N/A";

  const selectedBookingPayment = selectedBooking
    ? getBookingPaymentBreakdown(selectedBooking)
    : null;
  const selectedBookingStatus = String(selectedBooking?.status || "").toLowerCase();
  const canReassignSelectedBooking =
    !!selectedBooking &&
    !["completed", "cancelled"].includes(selectedBookingStatus);

  const selectedBookingCoords = selectedBookingResolvedCoords;
  const selectedBookingTerms = selectedBooking
    ? getBookingServiceTerms(selectedBooking)
    : [];

  const providersForSelectedBooking = (selectedBooking
    ? providers.filter((provider) => provider.id !== selectedBooking.providerId)
    : providers
  )
    .map((provider) => {
      const providerCoords = extractCoords(provider);
      const hasBookingCoords =
        Number.isFinite(selectedBookingCoords?.lat) &&
        Number.isFinite(selectedBookingCoords?.lng);
      const hasProviderCoords =
        Number.isFinite(providerCoords?.lat) &&
        Number.isFinite(providerCoords?.lng);

      const distance = hasBookingCoords && hasProviderCoords
        ? calculateDistance(
            selectedBookingCoords.lat,
            selectedBookingCoords.lng,
            providerCoords.lat,
            providerCoords.lng
          )
        : null;

      const serviceDetails = getProviderServiceDetails(provider);
      const serviceMatch = providerMatchesBookingService(
        provider,
        selectedBookingTerms
      );

      return {
        ...provider,
        distance,
        serviceDetails,
        serviceMatch,
      };
    })
    .filter((provider) => {
      if (!maxDistance || maxDistance <= 0) return true;
      if (!Number.isFinite(provider.distance)) return true;
      return provider.distance <= maxDistance;
    })
    .sort((providerA, providerB) => {
      if (providerA.serviceMatch !== providerB.serviceMatch) {
        return providerA.serviceMatch ? -1 : 1;
      }

      const aHasDistance = Number.isFinite(providerA.distance);
      const bHasDistance = Number.isFinite(providerB.distance);

      if (aHasDistance && bHasDistance) {
        return providerA.distance - providerB.distance;
      }
      if (aHasDistance) return -1;
      if (bHasDistance) return 1;

      return getProviderDisplayName(providerA)
        .toLowerCase()
        .localeCompare(getProviderDisplayName(providerB).toLowerCase());
    });

  const matchingProvidersForSelectedBooking = providersForSelectedBooking.filter(
    (provider) => provider.serviceMatch
  );

  const recommendedProvider =
    matchingProvidersForSelectedBooking[0] || providersForSelectedBooking[0] || null;

  const selectedProviderDetails = providersForSelectedBooking.find(
    (provider) => provider.id === selectedProviderId
  );

  useEffect(() => {
    if (!bookingModalOpen || !selectedBooking) return;

    if (
      selectedProviderId &&
      providersForSelectedBooking.some((provider) => provider.id === selectedProviderId)
    ) {
      return;
    }

    setSelectedProviderId(recommendedProvider?.id || "");
  }, [
    bookingModalOpen,
    selectedBooking,
    selectedProviderId,
    providersForSelectedBooking,
    recommendedProvider,
  ]);

  const handleOpenBookingModal = (booking) => {
    setSelectedBookingId(booking.id);
    setSelectedProviderId("");
    setBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    if (assigningProvider) return;
    setBookingModalOpen(false);
    setSelectedBookingId(null);
    setSelectedProviderId("");
  };

  const handleAssignNewProvider = async () => {
    if (!selectedBooking?.id) {
      setSnackbar({
        open: true,
        message: "No booking selected.",
        severity: "error",
      });
      return;
    }

    if (!canReassignSelectedBooking) {
      setSnackbar({
        open: true,
        message: "Completed or cancelled bookings cannot be reassigned.",
        severity: "error",
      });
      return;
    }

    if (!selectedProviderId) {
      setSnackbar({
        open: true,
        message: "Select a provider before assigning.",
        severity: "error",
      });
      return;
    }

    if (
      !providersForSelectedBooking.some(
        (provider) => provider.id === selectedProviderId
      )
    ) {
      setSnackbar({
        open: true,
        message: "Selected provider is not available in current Open Pool filters.",
        severity: "error",
      });
      return;
    }

    if (selectedProviderId === selectedBooking.providerId) {
      setSnackbar({
        open: true,
        message: "Selected provider is already assigned to this booking.",
        severity: "warning",
      });
      return;
    }

    setAssigningProvider(true);

    try {
      const response = await fetch(`${CLOUD_FUNCTION_BASE}/assignBookingToProvider`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId: selectedBooking.id,
          providerId: selectedProviderId,
        }),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || "Failed to assign provider.");
      }

      const assignedProvider = providersForSelectedBooking.find(
        (provider) => provider.id === selectedProviderId
      );

      setSnackbar({
        open: true,
        message: `Booking reassigned to ${getProviderDisplayName(assignedProvider)}.`,
        severity: "success",
      });
      setSelectedProviderId("");
    } catch (assignError) {
      console.error("Error assigning new provider:", assignError);
      setSnackbar({
        open: true,
        message: assignError?.message || "Failed to reassign booking.",
        severity: "error",
      });
    } finally {
      setAssigningProvider(false);
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return true;
    const cat = (
      b.category || b.serviceCategory || (b.service && b.service.category) || ""
    )
      .toString()
      .toLowerCase();
    if (cat.includes(q)) return true;
    // fallback: match service name/title
    const title = (b.serviceName || b.serviceTitle || (b.service && b.service.name) || "").toString().toLowerCase();
    if (title.includes(q)) return true;
    // match customer name/id/email
    const customer = (b.customerName || b.customerId || b.customerEmail || "").toString().toLowerCase();
    if (customer.includes(q)) return true;
    // match provider/client name/id
    const provider = (b.providerName || b.clientName || b.clientId || (b.service && b.service.providerName) || "").toString().toLowerCase();
    if (provider.includes(q)) return true;
    const coupon = (b.couponCode || "").toString().toLowerCase();
    if (coupon.includes(q)) return true;
    if (hasProviderNotReachedAlert(b)) {
      const alertSearchable = [
        "provider not reached",
        b.providerNotReachedAlert?.supportCaseCode || "",
        b.providerNotReachedAlert?.status || b.providerNotReachedAlertStatus || "",
      ]
        .join(" ")
        .toLowerCase();
      if (alertSearchable.includes(q)) return true;
    }
    return false;
  });

  const openNotReachedAlertsCount = bookings.filter((booking) =>
    hasProviderNotReachedAlert(booking)
  ).length;

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0 }}>
            Booking Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review bookings, payment breakdowns, provider assignment, and operational alerts.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${bookings.length} total`} size="small" variant="outlined" />
          <Chip
            label={`${filteredBookings.length} shown`}
            size="small"
            color="primary"
            variant="outlined"
          />
          {openNotReachedAlertsCount > 0 && (
            <Chip
              label={`${openNotReachedAlertsCount} attention`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {openNotReachedAlertsCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {openNotReachedAlertsCount} booking(s) flagged as "Provider Not Reached" and require immediate backend attention.
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1.5,
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "#f8fafc",
          }}
        >
          <TextField
            size="small"
            placeholder="Search order, service, customer, provider"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: { xs: "100%", sm: 420 }, backgroundColor: "background.paper" }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newView) => newView && setViewMode(newView)}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">Loading bookings...</Typography>
          </Box>
        ) : filteredBookings.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No bookings found</Typography>
          </Box>
        ) : viewMode === "list" ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {filteredBookings.map((booking) => {
              const payment = getBookingPaymentBreakdown(booking);
              return (
                <Box
                  key={booking.id}
                  onClick={() => handleOpenBookingModal(booking)}
                  sx={{
                    cursor: "pointer",
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    transition: "background-color 140ms ease, border-color 140ms ease",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                    "&:last-of-type": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(0, 1.45fr) minmax(0, 1fr) minmax(150px, 0.45fr)",
                      },
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800, lineHeight: 1.25, overflowWrap: "anywhere" }}
                        >
                          #{booking.orderNumber || booking.id}
                        </Typography>
                        <Chip
                          label={booking.status || "Unknown"}
                          color={getStatusColor(booking.status)}
                          size="small"
                        />
                        {hasProviderNotReachedAlert(booking) && (
                          <Chip label="Provider Not Reached" color="error" size="small" />
                        )}
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {getBookingTitle(booking)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        Created {formatDate(booking.createdAt)} | Scheduled{" "}
                        {formatDate(booking.scheduledDate || booking.bookingDate)}
                      </Typography>
                      {hasProviderNotReachedAlert(booking) && (
                        <Typography variant="caption" color="error.main">
                          Alert raised: {formatDateTime(
                            booking.providerNotReachedAlert?.reportedAt ||
                              booking.providerNotReachedAt
                          )}
                          {booking.providerNotReachedAlert?.supportCaseCode
                            ? ` | Case: ${booking.providerNotReachedAlert.supportCaseCode}`
                            : ""}
                        </Typography>
                      )}
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Customer
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {getCustomerLabel(booking)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Provider
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {getProviderLabel(booking)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                      <Typography variant="caption" color="text.secondary">
                        Total Charged
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {formatCurrency(payment.totalCharged)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Service {formatCurrency(payment.serviceFee)} + Platform{" "}
                        {formatCurrency(payment.platformFee)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {filteredBookings.map((booking) => {
              const payment = getBookingPaymentBreakdown(booking);
              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={booking.id}>
                  <Card
                    variant="outlined"
                    onClick={() => handleOpenBookingModal(booking)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      borderRadius: 2,
                      transition: "border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, lineHeight: 1.25, overflowWrap: "anywhere" }}
                          >
                            #{booking.orderNumber || booking.id}
                          </Typography>
                          <Chip
                            label={booking.status || "Unknown"}
                            color={getStatusColor(booking.status)}
                            size="small"
                          />
                        </Stack>
                        {hasProviderNotReachedAlert(booking) && (
                          <Chip label="Provider Not Reached" color="error" size="small" sx={{ alignSelf: "flex-start" }} />
                        )}
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Service
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {getBookingTitle(booking)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Customer
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {getCustomerLabel(booking)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Provider
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {getProviderLabel(booking)}
                            </Typography>
                          </Box>
                        </Box>
                        {(booking.couponCode || Number(booking.couponDiscount || 0) > 0) && (
                          <Chip
                            label={`Coupon: ${booking.couponCode || "Applied"} | Discount ${formatCurrency(Number(booking.couponDiscount || 0))}`}
                            size="small"
                            variant="outlined"
                            sx={{ alignSelf: "flex-start" }}
                          />
                        )}
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            p: 1.25,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Total Charged
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {formatCurrency(payment.totalCharged)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Service {formatCurrency(payment.serviceFee)} + Platform{" "}
                            {formatCurrency(payment.platformFee)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Scheduled {formatDate(booking.scheduledDate || booking.bookingDate)}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Dialog
        open={bookingModalOpen}
        onClose={handleCloseBookingModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Booking #{selectedBooking?.orderNumber || selectedBooking?.id || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedBooking?.serviceName || selectedBooking?.serviceTitle || "Service booking"}
              </Typography>
            </Box>
            {selectedBooking && (
              <Chip
                label={selectedBooking.status || "Unknown"}
                color={getStatusColor(selectedBooking.status)}
                size="small"
                sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f8fafc" }}>
          {!selectedBooking ? (
            <Typography color="text.secondary">Booking details are unavailable.</Typography>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.25,
                  backgroundColor: "background.paper",
                }}
              >
                {hasProviderNotReachedAlert(selectedBooking) && (
                  <Chip label="Provider Not Reached" color="error" size="small" />
                )}
                <Typography variant="body2" color="text.secondary">
                  Created: {formatDateTime(selectedBooking.createdAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated: {formatDateTime(selectedBooking.updatedAt)}
                </Typography>
              </Box>

              {selectedBookingPayment && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(5, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {[
                    ["Total Charged", selectedBookingPayment.totalCharged, true],
                    ["Service Fee", selectedBookingPayment.serviceFee],
                    ["Platform Fee", selectedBookingPayment.platformFee],
                    ["Service GST Included", selectedBookingPayment.gstIncluded],
                    ["Net Service Price", selectedBookingPayment.netServicePrice],
                  ].map(([label, value, primary]) => (
                    <Box
                      key={label}
                      sx={{
                        border: "1px solid",
                        borderColor: primary ? "primary.light" : "divider",
                        borderRadius: 2,
                        p: 1.25,
                        backgroundColor: primary ? "#eff6ff" : "background.paper",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                        {formatCurrency(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                        Service Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Service: {selectedBooking.serviceName || selectedBooking.serviceTitle || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Category: {selectedBooking.category || selectedBooking.serviceCategory || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Subcategory: {selectedBooking.subcategory || selectedBooking.leadSubcategory || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scheduled: {selectedBookingScheduledDate} {selectedBookingScheduledTime}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Address: {selectedBooking.address || selectedBooking.customerAddress || "N/A"}
                      </Typography>
                      {(selectedBooking.specialInstructions || selectedBooking.serviceDetails) && (
                        <Typography variant="body2" color="text.secondary">
                          Notes: {selectedBooking.specialInstructions || selectedBooking.serviceDetails}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                        Customer Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Name: {selectedBooking.customerName || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email: {selectedBooking.customerEmail || selectedBooking.email || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {selectedBooking.customerPhone || selectedBooking.phoneNumber || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Customer ID: {selectedBooking.customerId || selectedBooking.userId || "N/A"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                        Provider Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Name: {selectedBooking.providerName || selectedBooking.clientName || "Not assigned"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email: {selectedBooking.providerEmail || selectedBooking.clientEmail || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {selectedBooking.providerPhone || selectedBooking.clientPhone || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Provider ID: {selectedBooking.providerId || selectedBooking.clientId || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Assigned At: {formatDateTime(selectedBooking.assignedAt)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: "background.paper",
                }}
              >
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Cancel and Assign New Provider
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Current provider: {selectedBooking.providerName || selectedBooking.clientName || "Not assigned"}
                </Typography>

                {!canReassignSelectedBooking && (
                  <Alert severity="info">
                    This booking is {selectedBooking.status || "in a final state"} and cannot be reassigned.
                  </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="open-pool-distance-label">Open Pool Radius</InputLabel>
                    <Select
                      labelId="open-pool-distance-label"
                      value={maxDistance}
                      label="Open Pool Radius"
                      onChange={(event) => setMaxDistance(Number(event.target.value))}
                    >
                      <MenuItem value={0}>All Providers</MenuItem>
                      <MenuItem value={5}>Within 5 km</MenuItem>
                      <MenuItem value={10}>Within 10 km</MenuItem>
                      <MenuItem value={20}>Within 20 km</MenuItem>
                      <MenuItem value={30}>Within 30 km</MenuItem>
                      <MenuItem value={50}>Within 50 km</MenuItem>
                      <MenuItem value={100}>Within 100 km</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      label={`${providersForSelectedBooking.length} providers in pool`}
                      size="small"
                      color="default"
                    />
                    <Chip
                      label={`${matchingProvidersForSelectedBooking.length} service matched`}
                      size="small"
                      color={
                        matchingProvidersForSelectedBooking.length > 0
                          ? "success"
                          : "warning"
                      }
                    />
                  </Box>
                </Stack>

                {recommendedProvider && (
                  <Alert
                    severity={recommendedProvider.serviceMatch ? "success" : "warning"}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setSelectedProviderId(recommendedProvider.id)}
                      >
                        Use Recommended
                      </Button>
                    }
                  >
                    Recommended: {getProviderDisplayName(recommendedProvider)} ({formatDistanceLabel(recommendedProvider.distance)})
                    {recommendedProvider.serviceMatch
                      ? " - matches booking service"
                      : " - nearest available provider"}
                  </Alert>
                )}

                {providersLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading providers...
                    </Typography>
                  </Box>
                ) : (
                  <FormControl fullWidth size="small" disabled={!canReassignSelectedBooking || assigningProvider}>
                    <InputLabel id="booking-provider-select-label">New Provider</InputLabel>
                    <Select
                      labelId="booking-provider-select-label"
                      value={selectedProviderId}
                      label="New Provider"
                      onChange={(event) => setSelectedProviderId(event.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select provider...</em>
                      </MenuItem>
                      {providersForSelectedBooking.map((provider) => (
                        <MenuItem key={provider.id} value={provider.id}>
                          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {getProviderDisplayName(provider)} ({formatDistanceLabel(provider.distance)})
                            </Typography>
                            <Typography
                              variant="caption"
                              color={provider.serviceMatch ? "success.main" : "text.secondary"}
                              noWrap
                            >
                              {provider.serviceMatch ? "Service matched" : "No exact service match"}
                              {provider.email ? ` | ${provider.email}` : ""}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {!providersLoading && providersForSelectedBooking.length === 0 && (
                  <Alert severity="warning">
                    No providers available in the selected Open Pool radius for this booking.
                  </Alert>
                )}

                {selectedProviderDetails && (
                  <Card variant="outlined" sx={{ backgroundColor: "#fafcff" }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Provider Profile Preview
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Name: {getProviderDisplayName(selectedProviderDetails)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email: {selectedProviderDetails.email || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {selectedProviderDetails.phoneNumber || selectedProviderDetails.phone || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Distance: {formatDistanceLabel(selectedProviderDetails.distance)}
                      </Typography>
                      <Typography variant="body2" color={selectedProviderDetails.serviceMatch ? "success.main" : "warning.main"}>
                        Match: {selectedProviderDetails.serviceMatch ? "Service matched" : "Nearest available"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Services: {selectedProviderDetails.serviceDetails.slice(0, 4).join(", ") || "Not listed"}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleAssignNewProvider}
                  disabled={
                    !canReassignSelectedBooking ||
                    !selectedProviderId ||
                    assigningProvider ||
                    providersLoading
                  }
                >
                  {assigningProvider
                    ? "Assigning New Provider..."
                    : "Cancel Current & Assign New Provider"}
                </Button>
              </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBookingModal} disabled={assigningProvider}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete feature removed from UI */}

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Bookings;
